/*
 * Copyright (c) 2024 Roboto Technologies, Inc.
 */

import {
  Box,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import * as React from "react";

import { RobotoErrorAlertDialog } from "@/shared/components/RobotoErrorAlert";
import {
  AddCircleOutlineIcon,
  RemoveCircleOutlineIcon,
} from "@/shared/foundation/icons/mui";
import { useLazyAPICall } from "@/shared/services/apiHooks";
import {
  AuthZTuple,
  APIServiceError,
  GetAccessResponse,
  RobotoEndpoint,
} from "@/types";

interface AuthZPermissionCategoryProps {
  allowEdit: boolean;
  editAccessEndpoint: RobotoEndpoint;
  editAccessEndpointPathParams?: Record<string, string | number>;
  onChange?: () => void;
  relationName: string;
  relations: AuthZTuple[];
  resourceId: string;
  resourceType: "dataset" | "collection";
}

export const AuthZPermissionCategory: React.FC<AuthZPermissionCategoryProps> = (
  props,
) => {
  const theme = useTheme();
  const [addUserText, setAddUserText] = React.useState("");

  const [errorModalOpen, setErrorModalOpen] = React.useState(false);
  const [lastError, setLastError] = React.useState<APIServiceError | undefined>(
    undefined,
  );

  const { initiateRequest } = useLazyAPICall<GetAccessResponse>();

  const addRemoveRelation = async (
    user: string,
    relation: string,
    mode: "add" | "remove",
  ) => {
    const data = {
      [mode]: [
        {
          user: user,
          relation: relation,
          obj: `${props.resourceType}:${props.resourceId}`,
        },
      ],
    };

    const { error } = await initiateRequest({
      endpoint: props.editAccessEndpoint,
      method: "PUT",
      pathParams: props.editAccessEndpointPathParams,
      requestBody: JSON.stringify(data),
    });

    if (error) {
      setErrorModalOpen(true);
      setLastError(error);
    } else {
      setAddUserText("");
    }

    if (props.onChange) {
      props.onChange();
    }
  };

  let rowsForRelations;

  rowsForRelations = props.relations.map((relation) => {
    return (
      <Box key={`${relation.user}-${relation.relation}-${relation.obj}`}>
        <Typography variant={"body2"}>
          {relation.user}{" "}
          {props.allowEdit && (
            <IconButton
              size="small"
              sx={{ ml: theme.spacing(0.25) }}
              onClick={() =>
                void addRemoveRelation(
                  relation.user,
                  props.relationName,
                  "remove",
                )
              }
            >
              <RemoveCircleOutlineIcon color="primary" fontSize="small" />
            </IconButton>
          )}
        </Typography>
      </Box>
    );
  });

  // If there's no add button and no rows to display, show None
  if (!props.allowEdit && rowsForRelations.length === 0) {
    rowsForRelations = <Typography variant={"body2"}>None</Typography>;
  }

  let addButton = <></>;
  if (props.allowEdit) {
    addButton = (
      <Box>
        <RobotoErrorAlertDialog
          open={errorModalOpen}
          setOpen={setErrorModalOpen}
          error={lastError}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <TextField
            id="standard-size-small"
            size="small"
            variant="outlined"
            placeholder="Enter User"
            inputProps={{
              style: {
                fontSize: "0.9rem",
                width: "100px",
              },
              autoComplete: "off",
            }}
            sx={{
              width: "auto",
              mt: theme.spacing(0.5),
            }}
            value={addUserText}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setAddUserText(event.target.value);
            }}
            onKeyDown={(ev) => {
              if (ev.key === "Enter") {
                void addRemoveRelation(
                  addUserText || "",
                  props.relationName,
                  "add",
                );
                ev.preventDefault();
              }
            }}
          />
          <IconButton
            size="small"
            sx={{ ml: theme.spacing(0.25) }}
            onClick={() =>
              void addRemoveRelation(
                addUserText || "",
                props.relationName,
                "add",
              )
            }
          >
            <AddCircleOutlineIcon color="primary" fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {rowsForRelations}
      {addButton}
    </Box>
  );
};
